import AsyncGroupFactory from '@/Factories/AsyncGroupFactory';
import ComponentManager from '@/Management/ComponentManager';
import TabGroupBoxViewModelT from '@/ViewModels/Controls/TabGroupBoxViewModel';
import TabGroupBoxT from '@/Controls/TabGroupBox';
import TabStrip2ViewModelT from '@/ViewModels/Controls/TabStrip2ViewModel';
import * as CoreFrameDTOs from "coreframetypings/dto";
import StorageHelper from '@/Utility/StorageHelper';
import { ITabGroupFactoryConstructorParameter } from '#/DefaultViewExtensionInterfaces';

export default class TabGroupFactory extends AsyncGroupFactory {

    private TabGroupBoxViewModel = ComponentManager.getViewModel<typeof TabGroupBoxViewModelT>('TabGroupBoxViewModel');
    private TabGroupBox = ComponentManager.getControl<typeof TabGroupBoxT>('TabGroupBox');

    /**
     * @override
     */
    viewModel: TabGroupBoxViewModelT;

    /**
     * Der innenliegende TabStrip
     */
    public tabStrip: TabStrip2ViewModelT;

    private parentTabGroup: TabGroupBoxViewModelT;
    private tabIndex: number;

    public constructor(private readonly params: ITabGroupFactoryConstructorParameter) {
        super(params);
        this.colspan = this.paramOrDefault(params.colspan, 'Fill');
        this.layout = this.paramOrDefault(params.layout, 'OneColumnLayout');
        this.tabStrip = this.paramOrDefault(params.tabStrip, null);
        this.parentTabGroup = this.paramOrDefault(params.parentTabGroup, null);
        this.tabIndex = this.paramOrDefault(params.tabIndex, -1);
    }

    /**
     * @override 
     */
    protected createViewModel(): TabGroupBoxViewModelT {
        const viewModel = new this.TabGroupBoxViewModel({
            context: this.context,
            isVisible: this.isVisible,
            layout: this.layout,
            tabStrip: this.tabStrip,
            isSortable: true,
            selectedIndex: this.createSelectedIndexBinding(),
            firstGroupName: this.getFullTabGroupName(),
            isSummaryViewMode: this.isSummaryViewMode,
            detailViewGroup: this.detailViewGroup,
            detailViewGroups: this.getInnerTabGroups(this.detailViewGroup),
            locator: this.locator, // In diesem speziellen Fall werden die eigentlichen Tabs als Untergruppen modelliert; daher darf der Locator hier noch nicht angepasst werden
            parent: this.parentTabGroup,
            tabIndex: this.tabIndex,
        });

        if (this.tabStrip == null) {
            this.tabStrip = viewModel.tabStrip;
        }

        return viewModel;
    }

    /**
     * @override 
     */
    protected createControl(): TabGroupBoxT {
        return new this.TabGroupBox(this.viewModel);
    }

    /**
     * Liefert den aktuell selektierten Index zurück
     */
    private createSelectedIndexBinding(): KnockoutComputed<number> {
        const svMode = (this.isSummaryViewMode) ? 'SV_' : '';
        const groupName = this.getFullTabGroupName();
        const storageHelperKey = `TabGroupBoxSelectedIndex_${svMode}${groupName}`;
        return StorageHelper.bind(storageHelperKey, 0);
    }

    private getFullTabGroupName(): string {
        const mainTabGroup = this.params.parentDetailViewGroup || this.detailViewGroup;
        return this.getFullGroupName(mainTabGroup);
    }

    /**
     * Liefert die inneren TabGroups
     * @param detailViewGroup
     */
    private getInnerTabGroups(detailViewGroup: CoreFrameDTOs.IDetailViewGroupDTO): CoreFrameDTOs.IDetailViewGroupDTO[] {
        let result = [];

        if (detailViewGroup.detailViewGroups != null) {
            for (let i = 0; i < detailViewGroup.detailViewGroups.length; i++) {
                const innerGroup = detailViewGroup.detailViewGroups[i];

                if (innerGroup.uiGenerator.indexOf('TabGroup') === 0) {
                    result.push(innerGroup);
                }
            }
        }

        return result;
    }
}

ComponentManager.setFactory('TabGroup', TabGroupFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');